import { useEffect, useMemo, useState, useRef } from "react";
import { absoluteUrl } from "@/lib/api";
import { fetchHeroWithCache, getCachedHero } from "@/lib/hero-cache";

const IMAGE_INTERVAL_MS = 6500;
// Safety fallback: if a video's natural end never fires (e.g. broken metadata
// or a very long file), advance after this timeout so the slideshow never
// gets stuck on a single video when other slides are queued.
const VIDEO_MAX_MS = 30000;

/**
 * HeroSlideshow — background slideshow of images and/or videos.
 * Uses shared hero-cache module so Layout's prefetch warms the cache and
 * cross-page navigation is instant. Renders nothing (just the overlay) when
 * no hero media is configured for the page.
 */
export default function HeroSlideshow({ className = "", overlay = true, page = "home" }) {
  // Detect viewport bucket once at mount; if the user rotates a phone or
  // resizes past the breakpoint we re-evaluate.
  const [device, setDevice] = useState(
    () => (typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop"),
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setDevice(mq.matches ? "mobile" : "desktop");
    onChange();
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const cached = getCachedHero(page, device);
  const [media, setMedia] = useState(() => (cached && cached.length > 0 ? cached : []));
  const [loaded, setLoaded] = useState(!!cached);
  const [active, setActive] = useState(0);
  // Track whether the *initial* slide has finished its entrance animation so
  // subsequent slides use the slide-in animation instead of the first-entrance one.
  const [hasEntered, setHasEntered] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchHeroWithCache(page, device);
        if (cancelled) return;
        setMedia(list);
        // Reset the active slide when device changes so we don't try to show
        // an index that no longer exists in the new device-specific list.
        setActive(0);
      } catch (err) {
        if (!cancelled) {
          console.warn(`[HeroSlideshow] Failed to load hero media for page "${page}"`, err);
          setMedia([]);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [page, device]);

  const items = useMemo(() => media, [media]);
  const current = items[active];
  const multi = items.length > 1;
  const advance = () => {
    setHasEntered(true);
    setActive((i) => (i + 1) % items.length);
  };

  // Mark the entrance animation complete after its duration so subsequent
  // switches use the slide-in animation instead of the first-entrance one.
  useEffect(() => {
    if (items.length === 0 || hasEntered) return;
    const t = setTimeout(() => setHasEntered(true), 1900);
    return () => clearTimeout(t);
  }, [items.length, hasEntered]);

  // Preload the NEXT slide's media so the cross-fade is instant.
  useEffect(() => {
    if (items.length <= 1) return;
    const next = items[(active + 1) % items.length];
    if (!next?.url) return;
    if (next.type === "image") {
      const img = new Image();
      img.src = absoluteUrl(next.url);
    } else if (next.type === "video") {
      // Force the browser to start fetching metadata for the next video so
      // playback starts without a stall when it becomes active.
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      v.src = absoluteUrl(next.url);
    }
  }, [active, items]);

  // Auto-advance for images. Videos in a multi-slide set advance on their
  // natural end (with a safety timeout). A single video loops forever.
  useEffect(() => {
    if (!current || !multi) return;
    if (current.type === "image") {
      timerRef.current = setTimeout(advance, IMAGE_INTERVAL_MS);
    } else if (current.type === "video") {
      // Safety net in case the video's `ended` event never fires.
      timerRef.current = setTimeout(advance, VIDEO_MAX_MS);
    }
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, current, items.length]);

  return (
    <div className={`absolute inset-0 z-0 overflow-hidden ${className}`} data-testid="hero-slideshow">
      {items.map((m, i) => {
        const isActive = i === active;
        const src = absoluteUrl(m.url);
        const poster = absoluteUrl(m.poster_url);
        // Loop only when this is the *only* slide. In a multi-slide set we
        // want the video to end naturally so we can advance to the next.
        const shouldLoop = !multi;
        // Slide-wrapper animation:
        //  - single-slide setups get no wrapper animation (nothing to cycle to)
        //  - multi-slide setups slide the wrapper in from the right, out to the left
        const slideClass = multi
          ? isActive
            ? "hero-slide-active"
            : "hero-slide-inactive"
          : "";
        // Image/media animation:
        //  - first visible slide gets a soft blur-in zoom-back reveal
        //  - once entered, every subsequently-active image gets a continuous
        //    slow Ken Burns pan
        const mediaAnimClass = isActive
          ? hasEntered
            ? "hero-media-active"
            : "hero-media-first"
          : "";
        return (
          <div
            key={m.id || i}
            className={`absolute inset-0 will-change-transform ${
              isActive ? "opacity-100 z-[1]" : "opacity-0 z-0"
            } ${slideClass}`}
            aria-hidden={!isActive}
            data-testid={`hero-slide-${i}`}
          >
            {m.type === "video" ? (
              <video
                key={`${src}-${isActive ? "on" : "off"}`}
                className={`w-full h-full object-cover ${mediaAnimClass}`}
                src={src}
                poster={poster || undefined}
                autoPlay={isActive}
                muted
                playsInline
                loop={shouldLoop}
                preload={isActive ? "auto" : "metadata"}
                onEnded={(e) => {
                  if (shouldLoop) {
                    // Defensive loop for browsers that occasionally drop the
                    // loop attr (esp. after tab throttling).
                    try {
                      e.currentTarget.currentTime = 0;
                      e.currentTarget.play();
                    } catch { /* ignore playback rejection */ }
                    return;
                  }
                  // Part of a slideshow — hand off to the next slide.
                  if (isActive) advance();
                }}
              />
            ) : (
              <img
                key={`${src}-${isActive ? "on" : "off"}`}
                src={src}
                alt=""
                className={`w-full h-full object-cover ${mediaAnimClass}`}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={i === 0 ? "high" : "auto"}
              />
            )}
          </div>
        );
      })}

      {overlay && (
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/25 to-background pointer-events-none" />
      )}

      {/* Indicator dots (only when multiple) */}
      {items.length > 1 && loaded && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {items.map((_, i) => (
            <button
              key={`dot-${items[i]?.id || items[i]?.url || i}`}
              data-testid={`hero-dot-${i}`}
              onClick={() => setActive(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`transition-all duration-300 h-[3px] ${
                i === active ? "w-8 bg-primary" : "w-5 bg-foreground/30 hover:bg-foreground/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
